import React, { useRef, useEffect, useState } from 'react';
import { scaleBand, scaleLinear, max } from 'd3';

const DEFAULT_COLOR = '#1890ff';

interface Margin { top: number; right: number; bottom: number; left: number }

interface D3BarChartProps {
  data: Array<Record<string, any>>;
  xKey: string;
  yKey: string;
  layout?: 'horizontal' | 'vertical';
  color?: string;
  colors?: string[];
  barLabel?: string;
  height?: number;
  margin?: Margin;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
}

interface TooltipState {
  x: number;
  y: number;
  label: string;
  value: number;
}

interface Bar {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  value: number;
  fill: string;
}

export const D3BarChart: React.FC<D3BarChartProps> = ({
  data,
  xKey,
  yKey,
  layout = 'horizontal',
  color = DEFAULT_COLOR,
  colors,
  barLabel = 'Value',
  height = 300,
  margin,
  showGrid = true,
  showTooltip = true,
  showLegend = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth || 400);
    const ro = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data.length) return null;

  const defaultMargin: Margin = layout === 'vertical'
    ? { top: 10, right: 20, bottom: 20, left: 80 }
    : { top: 10, right: 20, bottom: 50, left: 40 };
  const m = margin ?? defaultMargin;

  const plotW = Math.max(width - m.left - m.right, 10);
  const plotH = Math.max(height - m.top - m.bottom, 10);

  const barFill = (i: number) => colors ? colors[i % colors.length] : color;

  let bars: Bar[] = [];
  let xAxisTicks: { x: number; label: string }[] = [];
  let yAxisTicks: { y: number; label: string }[] = [];
  let gridLines: { x1: number; y1: number; x2: number; y2: number }[] = [];

  if (layout === 'horizontal') {
    const cats = data.map(d => String(d[xKey]));
    const maxVal = max(data, d => Number(d[yKey])) ?? 0;

    const xScale = scaleBand().domain(cats).range([0, plotW]).padding(0.3);
    const yScale = scaleLinear().domain([0, maxVal]).range([plotH, 0]).nice();
    const yTickVals = yScale.ticks(5);

    bars = data.map((d, i) => {
      const cat = String(d[xKey]);
      const val = Number(d[yKey]);
      const bx = xScale(cat) ?? 0;
      const by = yScale(val);
      return { x: bx, y: by, w: xScale.bandwidth(), h: plotH - by, label: cat, value: val, fill: barFill(i) };
    });

    xAxisTicks = cats.map(cat => ({ x: (xScale(cat) ?? 0) + xScale.bandwidth() / 2, label: cat }));
    yAxisTicks = yTickVals.map(v => ({ y: yScale(v), label: String(v) }));
    gridLines = yTickVals.map(v => ({ x1: 0, x2: plotW, y1: yScale(v), y2: yScale(v) }));

  } else {
    // vertical layout: yKey = category (Y band scale), xKey = numeric value (X linear scale)
    const cats = data.map(d => String(d[yKey]));
    const maxVal = max(data, d => Number(d[xKey])) ?? 0;

    const yScale = scaleBand().domain(cats).range([0, plotH]).padding(0.3);
    const xScale = scaleLinear().domain([0, maxVal]).range([0, plotW]).nice();
    const xTickVals = xScale.ticks(5);

    bars = data.map((d, i) => {
      const cat = String(d[yKey]);
      const val = Number(d[xKey]);
      const by = yScale(cat) ?? 0;
      const bw = xScale(val);
      return { x: 0, y: by, w: bw, h: yScale.bandwidth(), label: cat, value: val, fill: barFill(i) };
    });

    yAxisTicks = cats.map(cat => ({
      y: (yScale(cat) ?? 0) + yScale.bandwidth() / 2,
      label: cat,
    }));
    xAxisTicks = xTickVals.map(v => ({ x: xScale(v), label: String(v) }));
    gridLines = xTickVals.map(v => ({ x1: xScale(v), x2: xScale(v), y1: 0, y2: plotH }));
  }

  const legendColor = colors ? colors[0] : color;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={width} height={height} onMouseLeave={() => setTooltip(null)}>
        <g transform={`translate(${m.left},${m.top})`}>
          {/* Grid lines */}
          {showGrid && gridLines.map((l, i) => (
            <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
              stroke="var(--mg-border, #e8e8e8)" strokeDasharray="3 3" />
          ))}

          {/* Bars */}
          {bars.map((b, i) => (
            <rect
              key={i}
              x={b.x} y={b.y}
              width={Math.max(b.w, 0)} height={Math.max(b.h, 0)}
              fill={b.fill}
              onMouseMove={showTooltip ? e => {
                const rect = containerRef.current!.getBoundingClientRect();
                setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: b.label, value: b.value });
              } : undefined}
              style={{ cursor: 'default' }}
            />
          ))}

          {/* Axes */}
          <line x1={0} y1={plotH} x2={plotW} y2={plotH} stroke="var(--mg-border, #ccc)" />
          <line x1={0} y1={0} x2={0} y2={plotH} stroke="var(--mg-border, #ccc)" />

          {/* X-axis tick labels */}
          {layout === 'horizontal' && xAxisTicks.map((t, i) => (
            <text
              key={i}
              x={t.x} y={plotH + 6}
              textAnchor="end"
              dominantBaseline="hanging"
              fontSize={11}
              fill="var(--mg-text-secondary, #666)"
              transform={`rotate(-35,${t.x},${plotH + 6})`}
              style={{ userSelect: 'none' }}
            >
              {t.label.length > 14 ? t.label.slice(0, 14) + '…' : t.label}
            </text>
          ))}
          {layout === 'vertical' && xAxisTicks.map((t, i) => (
            <text
              key={i}
              x={t.x} y={plotH + 6}
              textAnchor="middle"
              dominantBaseline="hanging"
              fontSize={11}
              fill="var(--mg-text-secondary, #666)"
              style={{ userSelect: 'none' }}
            >
              {t.label}
            </text>
          ))}

          {/* Y-axis tick labels */}
          {layout === 'horizontal' && yAxisTicks.map((t, i) => (
            <text
              key={i}
              x={-6} y={t.y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill="var(--mg-text-secondary, #666)"
              style={{ userSelect: 'none' }}
            >
              {t.label}
            </text>
          ))}
          {layout === 'vertical' && yAxisTicks.map((t, i) => (
            <text
              key={i}
              x={-6} y={t.y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill="var(--mg-text-secondary, #666)"
              style={{ userSelect: 'none' }}
            >
              {t.label.length > 12 ? t.label.slice(0, 12) + '…' : t.label}
            </text>
          ))}
        </g>
      </svg>

      {showTooltip && tooltip && (
        <div style={{
          position: 'absolute',
          left: tooltip.x + 12,
          top: tooltip.y - 36,
          background: 'var(--mg-bg-elevated, #fff)',
          border: '1px solid var(--mg-border, #e8e8e8)',
          borderRadius: 6,
          padding: '5px 10px',
          fontSize: 12,
          pointerEvents: 'none',
          zIndex: 10,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}>
          <strong>{tooltip.label}</strong>: {tooltip.value.toLocaleString()}
        </div>
      )}

      {showLegend && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          justifyContent: 'center',
          marginTop: 4,
          fontSize: 12,
        }}>
          <span style={{
            width: 16,
            height: 10,
            background: legendColor,
            display: 'inline-block',
            borderRadius: 2,
          }} />
          <span style={{ color: 'var(--mg-text-secondary, #666)' }}>{barLabel}</span>
        </div>
      )}
    </div>
  );
};
